import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import WebsiteLayout from '../components/layouts/WebsiteLayout';

interface TrainingVideo {
  id: string;
  title: string;
  description: string;
}

// Replace each VIDEO_ID_1, VIDEO_ID_2, etc. with the YouTube video ID (the part after watch?v= in a YouTube URL).
const schedulerVideos: TrainingVideo[] = [
  {
    id: 'gW2RhJas1vg',
    title: 'Onboarding Your Team',
    description: 'Let me show you where the easy-button is.',
  },
  {
    id: 'VIDEO_ID_2',
    title: 'Managing Details & Shifts',
    description: 'How to create, edit, and publish shifts so your team can see and accept them.',
  },
  {
    id: 'VIDEO_ID_3',
    title: 'Payments & Subscriptions',
    description: 'Understand how payments work, how to review receipts, and manage your subscription.',
  },
];

const werkerVideos: TrainingVideo[] = [
  {
    id: 'VIDEO_ID_4',
    title: 'Getting Started as a Werker',
    description: 'Set up your profile, connect with a scheduler, and accept your first shift.',
  },
  {
    id: 'VIDEO_ID_5',
    title: 'Viewing & Accepting Shifts',
    description: 'How to browse available shifts, accept offers, and manage your schedule.',
  },
  {
    id: 'VIDEO_ID_6',
    title: 'Clocking In & Out',
    description: 'Walk through the clock-in/clock-out flow and what happens after a shift ends.',
  },
];

type TabKey = 'schedulers' | 'werkers';

const TrainingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('schedulers');

  const videos = activeTab === 'schedulers' ? schedulerVideos : werkerVideos;

  return (
    <WebsiteLayout>
      <style>{`
        .training-heading { color: #1a1a1a; }
        .training-muted { color: #555555; }
        .training-tab-inactive { color: #555555; }
        @media (prefers-color-scheme: dark) {
          .training-heading { color: #ffffff; }
          .training-muted { color: #cccccc; }
          .training-tab-inactive { color: #cccccc; }
        }
      `}</style>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>

        {/* Page Header */}
        <h1 className="training-heading" style={{
          fontSize: '2rem',
          fontWeight: '700',
          marginBottom: '0.5rem',
          textAlign: 'center',
        }}>
          Training Videos
        </h1>
        <p className="training-muted" style={{
          fontSize: '1rem',
          textAlign: 'center',
          marginBottom: '2rem',
        }}>
          Step-by-step guides to help you get the most out of WerkApp.
        </p>

        {/* Tab Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.75rem',
          marginBottom: '2.5rem',
        }}>
          {(['schedulers', 'werkers'] as TabKey[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.6rem 1.8rem',
                borderRadius: '999px',
                border: `2px solid #f4b41a`,
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'background 0.2s, color 0.2s',
                backgroundColor: activeTab === tab ? '#f4b41a' : 'transparent',
                color: activeTab === tab ? '#1a1a1a' : undefined,
              }}
              className={activeTab !== tab ? 'training-tab-inactive' : undefined}
            >
              {tab === 'schedulers' ? 'Schedulers' : 'Werkers'}
            </button>
          ))}
        </div>

        {/* Video List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
          {videos.map((video) => (
            <div key={video.id}>
              {/* Video Info */}
              <h2 className="training-heading" style={{
                fontSize: '1.15rem',
                fontWeight: '700',
                margin: '0 0 0.3rem',
              }}>
                {video.title}
              </h2>
              <p className="training-muted" style={{
                fontSize: '0.95rem',
                margin: '0 0 0.75rem',
                lineHeight: '1.5',
              }}>
                {video.description}
              </p>

              {/* Responsive YouTube Embed */}
              <div style={{
                position: 'relative',
                paddingBottom: '56.25%', // 16:9
                height: 0,
                overflow: 'hidden',
                borderRadius: '10px',
                backgroundColor: '#1a1a1a',
              }}>
                <iframe
                  title={video.title}
                  src={`https://www.youtube.com/embed/${video.id}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              </div>
            </div>
          ))}
        </div>

      </div>
    </WebsiteLayout>
  );
};

export default TrainingPage;
